import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { stocktakeSessionsService, sitesService, auditorsService, multiLocationService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import {
  ClipboardList, Plus, ArrowLeft, Loader2, CalendarDays, MapPin, User,
  ChevronLeft, ChevronRight, Trash2, ExternalLink, Search, X, Warehouse,
  Lock
} from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth, subMonths, startOfQuarter, endOfQuarter, startOfYear, endOfYear } from 'date-fns';
import { MultiLocationSetup } from './MultiLocationComponents';
import { getApiError, formatDDMMYYYY } from '@/lib/utils';
import SitePicker from '../components/SitePicker';

const STATUS_CONFIG = {
  draft: { label: 'Draft', className: 'bg-slate-100 text-slate-700' },
  in_progress: { label: 'In Progress', className: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Completed', className: 'bg-green-100 text-green-700' },
  locked: { label: 'Locked', className: 'bg-amber-100 text-amber-800' },
  // Wave 5A P6: synthetic entry used only for PSB historical sessions
  psb_historical: { label: 'Completed \u2014 Imported history', className: 'bg-teal-100 text-teal-800' },
};

const SESSION_TYPE_CONFIG = {
  valuation: { label: 'Valuation', className: 'bg-emerald-100 text-emerald-700' },
  stock_audit: { label: 'Stock Audit', className: 'bg-purple-100 text-purple-700' },
};

export default function StocktakeSessionsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAdmin, isScheduler, isAuditor } = useAuth();
  // iter461 — Any internal user (admin / scheduler / auditor) can drive
  // the full Stocktake workflow end-to-end. Matches the backend
  // `is_stocktake_user` gate.
  const canManageStocktake = isAdmin || isScheduler || isAuditor;

  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const perPage = 20;
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [siteFilter, setSiteFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  // Default to current month so the table actually reflects the date filter.
  const [dateFrom, setDateFrom] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  const [activeQuickRange, setActiveQuickRange] = useState('this_month');

  // Quick-range presets
  const applyQuickRange = useCallback((key) => {
    const now = new Date();
    let from, to;
    if (key === 'this_month') { from = startOfMonth(now); to = endOfMonth(now); }
    else if (key === 'last_month') { const lm = subMonths(now, 1); from = startOfMonth(lm); to = endOfMonth(lm); }
    else if (key === 'this_quarter') { from = startOfQuarter(now); to = endOfQuarter(now); }
    else if (key === 'this_year') { from = startOfYear(now); to = endOfYear(now); }
    else if (key === 'all_time') {
      // "All" — no date filter; clear the date range inputs
      setDateFrom('');
      setDateTo('');
      setActiveQuickRange('all_time');
      setPage(1);
      return;
    }
    else { return; }
    setDateFrom(format(from, 'yyyy-MM-dd'));
    setDateTo(format(to, 'yyyy-MM-dd'));
    setActiveQuickRange(key);
    setPage(1);
  }, []);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sites, setSites] = useState([]);
  const [auditors, setAuditors] = useState([]);
  const [form, setForm] = useState({
    site_id: '',
    session_type: 'valuation',
    session_date: format(new Date(), 'yyyy-MM-dd'),
    period_from: '',
    period_to: '',
    auditor_id: '',
    notes: '',
    audit_mode: 'single',
  });
  const [multiLocConfig, setMultiLocConfig] = useState({ location_ids: [], auditor_assignments: {} });

  // iter370 #3 — Site stocktake defaults driving the create-session
  // policy. Rules from product brief:
  //   * If site.default_session_type === 'valuation': always create a
  //     valuation, no prompt, session_type/audit_mode fields hidden.
  //   * If site.default_session_type === 'stock_audit' (with or without
  //     default_audit_mode): user picks Valuation vs Stocktake at create
  //     time. Stocktake uses site.default_audit_mode.
  //   * If both NULL: block creation, ask user to set defaults first.
  const [siteDefaults, setSiteDefaults] = useState(null);
  const [siteDefaultsLoading, setSiteDefaultsLoading] = useState(false);

  // Auto-set dates when site changes for stock_audit
  const handleSiteChange = async (siteId) => {
    setForm(f => ({ ...f, site_id: siteId }));
    setSiteDefaults(null);
    if (!siteId) return;
    setSiteDefaultsLoading(true);
    // Pull the site's stocktake settings in parallel with the date logic.
    sitesService.getStocktakeSettings(parseInt(siteId))
      .then(s => {
        const defaults = {
          default_session_type: s?.default_session_type || null,
          default_audit_mode: s?.default_audit_mode || null,
        };
        setSiteDefaults(defaults);
        // Apply the strict-Valuation site policy immediately so the form
        // mirrors what will actually be created.
        if (defaults.default_session_type === 'valuation') {
          setForm(f => ({ ...f, session_type: 'valuation', audit_mode: 'single' }));
        } else if (defaults.default_session_type === 'stock_audit') {
          // Pre-fill stocktake + site default audit_mode (user can flip
          // to valuation via the new radio below).
          setForm(f => ({
            ...f,
            session_type: 'stock_audit',
            audit_mode: defaults.default_audit_mode || 'single',
          }));
        }
      })
      .catch(() => { setSiteDefaults({ default_session_type: null, default_audit_mode: null }); })
      .finally(() => setSiteDefaultsLoading(false));
    try {
      const data = await stocktakeSessionsService.getLastSessionDate(parseInt(siteId));
      if (data?.last_session_date) {
        const lastEnd = new Date(data.last_session_date + 'T00:00:00');
        const end = new Date(lastEnd);
        end.setDate(end.getDate() + 30);
        setForm(f => ({
          ...f,
          period_from: format(lastEnd, 'yyyy-MM-dd'),
          period_to: format(end, 'yyyy-MM-dd'),
          session_date: format(end, 'yyyy-MM-dd'),
        }));
      } else {
        const today = new Date();
        const end = new Date(today);
        end.setDate(end.getDate() + 30);
        setForm(f => ({
          ...f,
          period_from: format(today, 'yyyy-MM-dd'),
          period_to: format(end, 'yyyy-MM-dd'),
          session_date: format(today, 'yyyy-MM-dd'),
        }));
      }
    } catch {
      // Fallback: today + 30 days
      const today = new Date();
      const end = new Date(today);
      end.setDate(end.getDate() + 30);
      setForm(f => ({
        ...f,
        period_from: format(today, 'yyyy-MM-dd'),
        period_to: format(end, 'yyyy-MM-dd'),
        session_date: format(today, 'yyyy-MM-dd'),
      }));
    }
  };
  useEffect(() => {
    const loadRef = async () => {
      try {
        const [s, a] = await Promise.all([
          sitesService.list({ is_active: true }).catch(() => []),
          auditorsService.list({ is_active: true }).catch(() => []),
        ]);
        setSites(s || []);
        setAuditors(a || []);
      } catch { /* ignore */ }
    };
    loadRef();
  }, []);

  // Load sessions
  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (siteFilter !== 'all') params.site_id = siteFilter;
      if (typeFilter !== 'all') params.session_type = typeFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const data = await stocktakeSessionsService.list(params);
      setSessions(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast({ title: 'Error', description: 'Failed to load sessions', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, siteFilter, typeFilter, dateFrom, dateTo, toast]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Auto-default period_from is handled by handleSiteChange — no separate effect needed

  // Create session
  const handleCreate = async () => {
    if (!form.site_id) {
      toast({ title: 'Required', description: 'Please select a site', variant: 'destructive' });
      return;
    }
    // iter370 #3 — guard against creating without site defaults
    if (siteDefaults && !siteDefaults.default_session_type) {
      toast({
        title: 'Configure site defaults first',
        description: 'Open Site Setup → Stocktake Settings and pick a default session type before creating a session.',
        variant: 'destructive',
      });
      return;
    }
    if (form.audit_mode !== 'single' && multiLocConfig.location_ids.length < 1) {
      toast({ title: 'Required', description: 'Select at least one location for multi-location audit', variant: 'destructive' });
      return;
    }
    // iter-recovery-B — the Auditor field is only shown (and must be filled)
    // for single-site sessions; multi-location sessions assign auditors
    // per-location in the step below.
    const isMultiLocationSetup = form.session_type === 'stock_audit' && form.audit_mode !== 'single';
    if (!isMultiLocationSetup && !form.auditor_id) {
      toast({ title: 'Required', description: 'Please assign an auditor before creating this session', variant: 'destructive' });
      return;
    }
    setCreating(true);
    try {
      const payload = {
        site_id: parseInt(form.site_id),
        session_type: form.session_type,
        session_date: form.session_date,
        // iter408 — always create the parent in 'single' mode. If the
        // user picked a multi-location flow, the next step calls
        // enableMultiLocation() which transitions the parent and
        // creates the child sessions in one atomic call.
        audit_mode: 'single',
        is_multi_location_parent: isMultiLocationSetup,
        auditor_id: form.auditor_id ? parseInt(form.auditor_id) : undefined,
        period_from: form.period_from || undefined,
        period_to: form.period_to || undefined,
        notes: form.notes || undefined,
      };
      const session = await stocktakeSessionsService.create(payload);

      // If multi-location, enable it immediately
      if (form.audit_mode !== 'single' && multiLocConfig.location_ids.length > 0) {
        await multiLocationService.enableMultiLocation(session.id, {
          audit_mode: form.audit_mode,
          location_ids: multiLocConfig.location_ids,
          auditor_assignments: multiLocConfig.auditor_assignments,
        });
        toast({ title: 'Multi-Location Audit Created', description: `${session.session_reference} with ${multiLocConfig.location_ids.length} locations` });
      } else {
        toast({ title: 'Session Created', description: `${session.session_reference} created with ${session.line_count} count lines` });
      }

      setCreateOpen(false);
      setForm({ site_id: '', session_type: 'valuation', session_date: format(new Date(), 'yyyy-MM-dd'), period_from: '', period_to: '', auditor_id: '', notes: '', audit_mode: 'single' });
      setMultiLocConfig({ location_ids: [], auditor_assignments: {} });
      navigate(`/stocktaking/sessions/${session.id}`);
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to create session'), variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  // Delete session
  const handleDelete = async (e, sessionId, sessionStatus) => {
    e.stopPropagation();
    const msg = sessionStatus === 'locked'
      ? 'This session is locked. Are you sure you want to delete it?'
      : `Delete this ${sessionStatus} session? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await stocktakeSessionsService.delete(sessionId);
      toast({ title: 'Deleted', description: 'Session deleted' });
      loadSessions();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to delete'), variant: 'destructive' });
    }
  };

  // Unlock removed — `locked` is a terminal state (backend rejects
  // the transition; pre-deployment defect sweep #1).

  const clearFilters = () => {
    setStatusFilter('all');
    setSiteFilter('all');
    setTypeFilter('all');
    // Reset to default (this month) rather than empty so the table stays
    // bounded by a sensible date window — matches the new UX.
    applyQuickRange('this_month');
  };

  const hasActiveFilters = statusFilter !== 'all' || siteFilter !== 'all' || typeFilter !== 'all' || activeQuickRange !== 'this_month';
  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="space-y-6" data-testid="stocktake-sessions-page">
      <Toaster />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/stocktaking/dashboard')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ClipboardList className="h-6 w-6 text-emerald-600" />
              Stocktake Sessions
              {total > 0 && (
                <span className="text-sm font-normal text-muted-foreground ml-1" data-testid="session-total-count">
                  ({total} session{total !== 1 ? 's' : ''})
                </span>
              )}
            </h1>
            <p className="text-sm text-muted-foreground">Create and manage stock count sessions</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canManageStocktake && (
            <Button onClick={() => setCreateOpen(true)} data-testid="create-session-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Session
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-40">
              <Label className="text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger data-testid="status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="locked">Locked</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Label className="text-xs">Site</Label>
              <SitePicker
                sites={sites}
                value={siteFilter}
                onChange={(v) => { setSiteFilter(v); setPage(1); }}
                placeholder="All Sites"
                testId="site-filter"
                showAllOption
                allLabel="All Sites"
              />
            </div>
            <div className="w-40">
              <Label className="text-xs">Type</Label>
              <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
                <SelectTrigger data-testid="type-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="valuation">Valuation</SelectItem>
                  <SelectItem value="stock_audit">Stock Audit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-36">
              <Label className="text-xs">Date From</Label>
              <Input type="date" value={dateFrom}
                onChange={e => { setDateFrom(e.target.value); setActiveQuickRange(null); setPage(1); }}
                data-testid="filter-date-from" />
            </div>
            <div className="w-36">
              <Label className="text-xs">Date To</Label>
              <Input type="date" value={dateTo}
                onChange={e => { setDateTo(e.target.value); setActiveQuickRange(null); setPage(1); }}
                data-testid="filter-date-to" />
            </div>
            <div className="flex items-end gap-1.5 flex-wrap">
              {[
                { k: 'this_month', l: 'This Month' },
                { k: 'last_month', l: 'Last Month' },
                { k: 'this_quarter', l: 'Quarter' },
                { k: 'this_year', l: 'Year' },
                { k: 'all_time', l: 'All' },
              ].map(r => (
                <Button
                  key={r.k}
                  type="button"
                  size="sm"
                  variant={activeQuickRange === r.k ? 'default' : 'outline'}
                  onClick={() => applyQuickRange(r.k)}
                  className="h-9 text-xs"
                  data-testid={`quick-range-${r.k}`}
                >
                  {r.l}
                </Button>
              ))}
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground" data-testid="clear-filters-btn">
                <X className="h-4 w-4 mr-1" /> Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sessions Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No sessions found</p>
              <p className="text-sm mt-1">Create a new session to start counting</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Reference</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Auditor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Progress</TableHead>
                    <TableHead className="w-20"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.filter(s => !s.parent_session_id).map(s => {
                    // Wave 5A P6: PSB historical sessions display "Completed — Imported history"
                    // regardless of their underlying locked/completed status value.
                    const statusCfg = s.is_psb_historical
                      ? STATUS_CONFIG.psb_historical
                      : (STATUS_CONFIG[s.status] || STATUS_CONFIG.draft);
                    const typeCfg = SESSION_TYPE_CONFIG[s.session_type] || SESSION_TYPE_CONFIG.valuation;
                    const isMultiLoc = s.audit_mode && s.audit_mode !== 'single';
                    return (
                      <TableRow
                        key={s.id}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => navigate(`/stocktaking/sessions/${s.id}`)}
                        data-testid={`session-row-${s.id}`}
                      >
                        <TableCell className="font-mono font-medium">
                          {s.session_reference}
                          {isMultiLoc && (
                            <Badge variant="outline" className="ml-1.5 text-[9px] bg-indigo-50 text-indigo-700 border-indigo-200">
                              <Warehouse className="h-2.5 w-2.5 mr-0.5" />
                              {s.audit_mode === 'multi_with_cellar' ? 'Multi+Cellar' : 'Multi'}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={typeCfg.className}>{typeCfg.label}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                            {s.site_name || 'Unknown'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                            {s.session_type === 'stock_audit' ? (
                              <span className="text-xs">
                                {/* iter426C #9 — DD/MM/YYYY display format */}
                                {formatDDMMYYYY(s.period_from)}
                                {' \u2013 '}
                                {formatDDMMYYYY(s.period_to)}
                              </span>
                            ) : (
                              <span>{formatDDMMYYYY(s.session_date)}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <User className="h-3.5 w-3.5 text-muted-foreground" />
                            {s.auditor_name ? (
                              s.auditor_name
                            ) : s.is_psb_imported ? (
                              <span
                                className="text-teal-700 bg-teal-50 border border-teal-200 rounded px-1.5 py-0.5 text-xs font-medium"
                                title={s.notes || 'Imported from a PSB historical conversion — no physical auditor counted this session'}
                                data-testid={`auditor-imported-${s.id}`}
                              >
                                Imported
                              </span>
                            ) : (
                              'Unassigned'
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusCfg.className}>{statusCfg.label}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-sm">
                            {s.counted_lines}/{s.line_count}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" onClick={() => navigate(`/stocktaking/sessions/${s.id}`)}
                              data-testid={`open-session-${s.id}`}>
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                            {/* PSB historical sessions are permanently protected — show lock icon only */}
                            {s.is_psb_historical ? (
                              <span title="Locked imported history — cannot be deleted" className="flex items-center px-2 text-muted-foreground/50">
                                <Lock className="h-4 w-4" />
                              </span>
                            ) : (
                              (s.status !== 'locked' || isAdmin) && canManageStocktake && (
                                <Button variant="ghost" size="icon" onClick={(e) => handleDelete(e, s.id, s.status)}
                                  className="text-red-500 hover:text-red-700" data-testid={`delete-session-${s.id}`}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              {total > perPage && (
                <div className="flex items-center justify-between p-4 border-t">
                  <p className="text-sm text-muted-foreground" data-testid="pagination-info">
                    Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      data-testid="pagination-prev-btn"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" /> Prev
                    </Button>
                    <span className="text-sm self-center" data-testid="pagination-page-indicator">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      data-testid="pagination-next-btn"
                    >
                      Next <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Session Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle>Create Stocktake Session</DialogTitle>
            <DialogDescription>Select a site, session type, and date to begin a new stock count.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0 pr-1">
            <div className="space-y-2">
              <Label>Site *</Label>
              <SitePicker
                sites={sites}
                value={form.site_id}
                onChange={handleSiteChange}
                placeholder="Search for a site..."
                testId="create-site-select"
              />
            </div>
            {/* iter370 #3 — Session-type policy driven by site defaults.
                  • Valuation-only sites: lock to Valuation, no prompt.
                  • Stocktake sites:       allow flip to Valuation.
                  • No defaults set:       block create with a clear CTA. */}
            {form.site_id && siteDefaultsLoading && (
              <div className="text-xs text-muted-foreground flex items-center gap-2" data-testid="site-defaults-loading">
                <Loader2 className="h-3 w-3 animate-spin" /> Reading site stocktake defaults…
              </div>
            )}
            {form.site_id && !siteDefaultsLoading && siteDefaults &&
              !siteDefaults.default_session_type && (
              <div
                className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-2"
                data-testid="site-defaults-missing"
              >
                <p className="font-medium">No stocktake defaults configured for this site.</p>
                <p className="text-xs">
                  Open <span className="font-mono">Site Setup → Stocktake Settings</span> and choose
                  a default session type (Valuation or Stocktake) before creating a session.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-amber-300"
                  onClick={() => navigate(`/stocktaking/site-settings?site=${form.site_id}`)}
                  data-testid="site-defaults-cta"
                >
                  Configure site defaults
                </Button>
              </div>
            )}
            {form.site_id && !siteDefaultsLoading &&
              siteDefaults?.default_session_type === 'valuation' && (
              <div
                className="rounded-md border border-emerald-200 bg-emerald-50 p-2.5 text-xs text-emerald-800"
                data-testid="site-defaults-valuation-banner"
              >
                Site default: <strong>Valuation only</strong>. This session will start as a Valuation.
              </div>
            )}
            {form.site_id && !siteDefaultsLoading &&
              siteDefaults?.default_session_type === 'stock_audit' && (
              <div className="space-y-2">
                <Label>Session Type *</Label>
                <Select value={form.session_type} onValueChange={v => setForm(f => ({ ...f, session_type: v }))}>
                  <SelectTrigger data-testid="create-session-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="valuation">Valuation</SelectItem>
                    <SelectItem value="stock_audit">Stock Audit</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">
                  Site default is <strong>Stocktake</strong> — flip to Valuation if needed.
                </p>
              </div>
            )}
            {/* Audit Structure — only for Stock Audit, and only when site defaults allow it */}
            {form.session_type === 'stock_audit' &&
              siteDefaults?.default_session_type === 'stock_audit' && (
              <div className="space-y-2">
                <Label>Audit Structure</Label>
                <Select value={form.audit_mode} onValueChange={v => setForm(f => ({ ...f, audit_mode: v }))}>
                  <SelectTrigger data-testid="create-audit-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single Result (standard)</SelectItem>
                    <SelectItem value="multi_with_cellar">Multi-Location with Cellar</SelectItem>
                    <SelectItem value="multi_no_cellar">Multi-Location (no Cellar)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">
                  {form.audit_mode === 'single' && 'Standard single-site stock audit — one count, one result.'}
                  {form.audit_mode === 'multi_with_cellar' && 'Multiple locations with a central cellar/mainstock that receives supplier deliveries.'}
                  {form.audit_mode === 'multi_no_cellar' && 'Multiple locations counted independently — no central cellar.'}
                </p>
              </div>
            )}
            {form.session_type === 'stock_audit' ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Opening Date *</Label>
                  <Input type="date" value={form.period_from}
                    onChange={e => setForm(f => ({ ...f, period_from: e.target.value }))}
                    data-testid="create-period-from" />
                </div>
                <div className="space-y-2">
                  <Label>Closing / Count Date *</Label>
                  <Input type="date" value={form.period_to || form.session_date}
                    onChange={e => setForm(f => ({ ...f, period_to: e.target.value, session_date: e.target.value }))}
                    data-testid="create-period-to" />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Count Date *</Label>
                <Input type="date" value={form.session_date}
                  onChange={e => setForm(f => ({ ...f, session_date: e.target.value }))}
                  data-testid="create-session-date" />
              </div>
            )}
            {/* Auditor — hidden for multi-location (assigned per location instead) */}
            {!(form.session_type === 'stock_audit' && form.audit_mode !== 'single') && (
            <div className="space-y-2">
              <Label>Auditor *</Label>
              <Select value={form.auditor_id || "unassigned"} onValueChange={v => setForm(f => ({ ...f, auditor_id: v === 'unassigned' ? '' : v }))}>
                <SelectTrigger data-testid="create-auditor-select"><SelectValue placeholder="Assign auditor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned" data-testid="create-auditor-unassigned-option">Unassigned</SelectItem>
                  {auditors.map(a => (
                    <SelectItem key={a.user_id || a.id} value={String(a.user_id || a.id)}>{a.first_name} {a.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!form.auditor_id && (
                <p className="text-xs text-amber-600" data-testid="create-auditor-required-hint">
                  An auditor is required to create this session.
                </p>
              )}
            </div>
            )}
            {/* Multi-location setup — only when multi mode selected */}
            {form.session_type === 'stock_audit' && form.audit_mode !== 'single' && form.site_id && (
              <MultiLocationSetup
                siteId={form.site_id}
                auditors={auditors}
                onConfigChange={setMultiLocConfig}
              />
            )}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input placeholder="Optional notes" value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                data-testid="create-notes" />
            </div>
          </div>
          <DialogFooter className="shrink-0 border-t pt-4 mt-2">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={
                creating ||
                // iter370 #3 — block create when site defaults are missing
                (!!form.site_id && !siteDefaultsLoading && siteDefaults &&
                 !siteDefaults.default_session_type) ||
                // iter-recovery-B — block create until an auditor is assigned
                // for single-site sessions (multi-location assigns per-location)
                (!(form.session_type === 'stock_audit' && form.audit_mode !== 'single') && !form.auditor_id)
              }
              data-testid="confirm-create-btn"
            >
              {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
